import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const animationInitializedRef = useRef(false);

  useEffect(() => {
    if (animationInitializedRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    animationInitializedRef.current = true;

    let width = 0;
    let height = 0;
    let ctx: CanvasRenderingContext2D | null = null;
    let points: Array<any> = [];
    let target = { x: 0, y: 0 };
    let animateHeader = true;

    const initHeader = () => {
      width = window.innerWidth;
      height = canvas.offsetHeight || 400;

      // If footer is not laid out yet, retry
      if (!height || height < 80) {
        setTimeout(() => {
          initHeader();
        }, 300);
        return;
      }

      target = { x: width / 2, y: height / 2 };

      canvas.width = width;
      canvas.height = height;
      ctx = canvas.getContext('2d');
      if (!ctx) return;

      points = [];
      for (let x = 0; x < width; x = x + width / 20) {
        for (let y = 0; y < height; y = y + height / 20) {
          const px = x + Math.random() * (width / 20);
          const py = y + Math.random() * (height / 20);
          const p: any = {
            x: px,
            originX: px,
            y: py,
            originY: py,
            active: 0,
          };
          points.push(p);
        }
      }

      for (let i = 0; i < points.length; i++) {
        const closest: any[] = [];
        const p1 = points[i];

        for (let j = 0; j < points.length; j++) {
          const p2 = points[j];
          if (!(p1 === p2)) {
            let placed = false;

            for (let k = 0; k < 5; k++) {
              if (!placed) {
                if (closest[k] === undefined) {
                  closest[k] = p2;
                  placed = true;
                }
              }
            }

            for (let k = 0; k < 5; k++) {
              if (!placed) {
                if (getDistance(p1, p2) < getDistance(p1, closest[k])) {
                  closest[k] = p2;
                  placed = true;
                }
              }
            }
          }
        }

        p1.closest = closest;
      }

      for (let i = 0; i < points.length; i++) {
        const c = new Circle(points[i], 2 + Math.random() * 2, 'rgba(156,217,249,0.3)');
        points[i].circle = c;
      }
    };

    const addListeners = () => {
      const onMouseMove = (e: MouseEvent) => {
        const rect = canvas.getBoundingClientRect();
        target.x = e.clientX - rect.left;
        target.y = e.clientY - rect.top;
      };

      const onScroll = () => {
        if (window.pageYOffset > height) animateHeader = false;
        else animateHeader = true;
      };

      const onResize = () => {
        width = window.innerWidth;
        height = canvas.offsetHeight || 400;
        canvas.width = width;
        canvas.height = height;
        ctx = canvas.getContext('2d');
      };

      if (!('ontouchstart' in window)) {
        window.addEventListener('mousemove', onMouseMove);
      }
      window.addEventListener('scroll', onScroll);
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
      };
    };

    function getDistance(p1: any, p2: any) {
      return Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2);
    }

    class Circle {
      pos: any;
      radius: number | null;
      color: string | null;
      active: number = 0;

      constructor(pos: any, rad: number, color: string) {
        this.pos = pos || null;
        this.radius = rad || null;
        this.color = color || null;
      }

      draw() {
        if (!this.active || !ctx || !this.radius) return;
        ctx.beginPath();
        ctx.arc(this.pos.x, this.pos.y, this.radius, 0, 2 * Math.PI, false);
        ctx.fillStyle = 'rgba(156,217,249,' + this.active + ')';
        ctx.fill();
      }
    }

    const shiftPoint = (p: any) => {
      const duration = 1000 + Math.random() * 1000;
      const startTime = Date.now();
      const startX = p.x;
      const startY = p.y;
      const endX = p.originX - 50 + Math.random() * 100;
      const endY = p.originY - 50 + Math.random() * 100;

      const animateShift = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const ease =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        p.x = startX + (endX - startX) * ease;
        p.y = startY + (endY - startY) * ease;

        if (progress < 1) requestAnimationFrame(animateShift);
        else shiftPoint(p);
      };

      animateShift();
    };

    const drawLines = (p: any) => {
      if (!p.active || !ctx) return;
      for (const i in p.closest) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.closest[i].x, p.closest[i].y);
        ctx.strokeStyle = 'rgba(156,217,249,' + p.active + ')';
        ctx.stroke();
      }
    };

    const animate = () => {
      if (animateHeader && ctx) {
        ctx.clearRect(0, 0, width, height);

        for (let i = 0; i < points.length; i++) {
          const distance = getDistance(target, points[i]);

          if (distance < 2000) {
            points[i].active = 0.4;
            points[i].circle.active = 0.8;
          } else if (distance < 5000) {
            points[i].active = 0.2;
            points[i].circle.active = 0.5;
          } else if (distance < 10000) {
            points[i].active = 0.1;
            points[i].circle.active = 0.3;
          } else if (distance < 20000) {
            points[i].active = 0.05;
            points[i].circle.active = 0.1;
          } else {
            points[i].active = 0.01;
            points[i].circle.active = 0.02;
          }

          drawLines(points[i]);
          points[i].circle.draw();
        }
      }

      animationIdRef.current = requestAnimationFrame(animate);
    };

    try {
      initHeader();

      for (let i = 0; i < points.length; i++) {
        shiftPoint(points[i]);
      }

      const cleanupListeners = addListeners();
      animationIdRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationIdRef.current !== null) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
        cleanupListeners?.();
        animationInitializedRef.current = false;
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Footer canvas animation initialization error:', error);
      animationInitializedRef.current = false;
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#660033] text-white relative overflow-hidden min-h-[320px]">
      {/* Animated background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full z-0 pointer-events-none"
        style={{ background: '#660033' }}
        aria-hidden="true"
      />
      {/* Main Footer Content */}
      <div className="relative z-10 container mx-auto px-4 py-4">
        {/* Social Media Section */}
        <div className="text-center mb-12">
          <h4 className="text-lg font-semibold text-white mb-6">Follow Us</h4>
          <div className="flex justify-center space-x-4">
            <a
              href="https://www.facebook.com/intenseinfotech"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
              aria-label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 12c0-5.522-4.477-10-10-10S2 6.478 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898v-2.89h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.463h-1.26c-1.243 0-1.63.771-1.63 1.562v1.875h2.773l-.443 2.89h-2.33v6.987C18.343 21.128 22 16.991 22 12z"/>
              </svg>
            </a>
            <a
              href="https://twitter.com/intenseinfotech"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-400 transition-colors"
              aria-label="Twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2H21l-6.53 7.46L22 22h-6.41l-4.7-6.18L5.52 22H3l7.08-8.08L2 2h6.59l4.24 5.64L18.24 2h.004zM8.35 4H5.92l9.86 13.19h2.43L8.35 4z"/>
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/intense-infotech/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3A2.94 2.94 0 0 1 22 6v12a2.94 2.94 0 0 1-3 3H5a2.94 2.94 0 0 1-3-3V6a2.94 2.94 0 0 1 3-3h14zM8.34 18v-7.37H6V18h2.34zm-1.19-8.42a1.36 1.36 0 1 0 0-2.72 1.36 1.36 0 0 0 0 2.72zM20 18v-4c0-2.16-1.15-3.17-2.7-3.17a2.32 2.32 0 0 0-2.1 1.15h-.03v-.98H12V18h2.34v-3.72c0-.98.18-1.93 1.4-1.93 1.2 0 1.21 1.12 1.21 2V18H20z"/>
              </svg>
            </a>
            <a
              href="https://www.instagram.com/intenseinfotech/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-colors"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.281H7.721c-.49 0-.875.385-.875.875v8.958c0 .49.385.875.875.875h8.558c.49 0 .875-.385.875-.875V8.582c0-.49-.385-.875-.875-.875z"/>
              </svg>
            </a>
          </div>
        </div>
        {/* Services Menu */}
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-gray-300">
            <div>
              <h5 className="text-white font-semibold mb-3">Services</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/web-development-services" className="hover:text-blue-400">Web Development</Link>
                </li>
               
                <li>
                  <Link to="/web-design-services" className="hover:text-blue-400">Web Design</Link>
                </li>
                <li>
                  <Link to="/wordpress-development" className="hover:text-blue-400">WordPress Development</Link>
                </li>
                <li>
                  <Link to="/react-development" className="hover:text-blue-400">React Development</Link>
                </li>
                <li>
                  <Link to="/custom-web-applications" className="hover:text-blue-400">Custom Web Applications</Link>
                </li>
                <li>
                  <Link to="/enterprise-app" className="hover:text-blue-400">Enterprise Applications</Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-3">Marketing & SEO</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/digital-marketing" className="hover:text-blue-400">Digital Marketing</Link>
                </li>
                <li>
                  <Link to="/seo" className="hover:text-blue-400">SEO</Link>
                </li>
                <li>
                  <Link to="/seo-services" className="hover:text-blue-400">SEO Services</Link>
                </li>
                <li>
                  <Link to="/search-engine-marketing" className="hover:text-blue-400">Search Engine Marketing</Link>
                </li>
                <li>
                  <Link to="/smo" className="hover:text-blue-400">Social Media Management</Link>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-white font-semibold mb-3">Media & Other</h5>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/video-services" className="hover:text-blue-400">Video Services</Link>
                </li>
                <li>
                  <Link to="/media-services" className="hover:text-blue-400">Media Services</Link>
                </li>
                <li>
                  <Link to="/branding-services" className="hover:text-blue-400">Branding Services</Link>
                </li>
                <li>
                  <Link to="/mobile-app-development" className="hover:text-blue-400">Mobile App Development</Link>
                </li>
                <li>
                  <Link to="/ecommerce" className="hover:text-blue-400">Ecommerce Development</Link>
                </li>
                <li>
                  <Link to="/maintenance" className="hover:text-blue-400">Maintenance</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2024 Intense Infotech. All rights reserved. | 
              <span className="ml-2">Crafted with ❤️ in India</span>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-blue-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="text-gray-400 hover:text-blue-400 transition-colors">
                Cookie Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-blue-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
